// Composants d'interface partagés, construits sur la charte (styles/components.css et app.css).
import {
  useEffect,
  useId,
  useRef,
  useState,
  createContext,
  useContext,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Link } from "react-router";
import { ApiError } from "../api";
import { fileSize } from "../format";

// ---------------------------------------------------------------------------
// Boutons

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primaire" | "secondaire" | "discret" | "danger";
  small?: boolean;
  busy?: boolean;
};

export function Button({ variant = "primaire", small, busy, className = "", children, disabled, type = "button", ...rest }: BtnProps) {
  return (
    <button
      type={type}
      className={`bv-btn bv-btn-${variant === "danger" ? "secondaire bv-btn-danger" : variant} ${small ? "bv-btn-petit" : ""} ${className}`}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export function ButtonLink({ to, variant = "primaire", small, children }: { to: string; variant?: "primaire" | "secondaire" | "discret"; small?: boolean; children: ReactNode }) {
  return (
    <Link to={to} className={`bv-btn bv-btn-${variant} ${small ? "bv-btn-petit" : ""}`}>
      {children}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Champs

type FieldShell = { label: string; help?: ReactNode; error?: string; required?: boolean; children: (id: string, describedBy?: string) => ReactNode };

function Shell({ label, help, error, required, children }: FieldShell) {
  const id = useId();
  const helpId = `${id}-aide`;
  return (
    <div className={`bv-champ ${error ? "bv-champ-erreur" : ""}`}>
      <label htmlFor={id}>
        {label}
        {required ? <span className="req" aria-hidden="true"> *</span> : null}
      </label>
      {children(id, help || error ? helpId : undefined)}
      {error ? (
        <p className="bv-champ-aide" id={helpId} role="alert">
          {error}
        </p>
      ) : help ? (
        <p className="bv-champ-aide" id={helpId}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label: string; help?: ReactNode; error?: string };
export function Input({ label, help, error, required, ...rest }: InputProps) {
  return (
    <Shell label={label} help={help} error={error} required={required}>
      {(id, d) => <input id={id} aria-describedby={d} aria-invalid={error ? true : undefined} required={required} {...rest} />}
    </Shell>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & { label: string; help?: ReactNode; error?: string; options: { value: string; label: string }[] };
export function Select({ label, help, error, options, required, ...rest }: SelectProps) {
  return (
    <Shell label={label} help={help} error={error} required={required}>
      {(id, d) => (
        <select id={id} aria-describedby={d} aria-invalid={error ? true : undefined} required={required} {...rest}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Shell>
  );
}

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; help?: ReactNode; error?: string };
export function Textarea({ label, help, error, required, ...rest }: TextareaProps) {
  return (
    <Shell label={label} help={help} error={error} required={required}>
      {(id, d) => <textarea id={id} aria-describedby={d} aria-invalid={error ? true : undefined} required={required} rows={4} {...rest} />}
    </Shell>
  );
}

export function Checkbox({ label, checked, onChange, help }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; help?: ReactNode }) {
  const id = useId();
  return (
    <div className="checkbox">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <label htmlFor={id}>
        {label}
        {help ? <span className="muted small block">{help}</span> : null}
      </label>
    </div>
  );
}

/** Champ montant en euros (saisie française), valeur en centimes. */
export function MoneyInput({ label, cents, onChange, help, error, required }: { label: string; cents: number | null; onChange: (c: number | null) => void; help?: ReactNode; error?: string; required?: boolean }) {
  const [text, setText] = useState(cents === null ? "" : (cents / 100).toFixed(2).replace(".", ","));
  const [local, setLocal] = useState<string | undefined>();
  return (
    <Input
      label={label}
      inputMode="decimal"
      value={text}
      help={help}
      required={required}
      error={error ?? local}
      onChange={(e) => {
        setText(e.target.value);
        const v = e.target.value.trim();
        if (v === "") {
          setLocal(undefined);
          onChange(null);
          return;
        }
        const cleaned = v.replace(/[\s  €]/g, "").replace(",", ".");
        if (/^\d+(\.\d{1,2})?$/.test(cleaned)) {
          setLocal(undefined);
          const [i, d = ""] = cleaned.split(".");
          onChange(parseInt(i, 10) * 100 + parseInt((d + "00").slice(0, 2), 10));
        } else {
          setLocal("Indiquez un montant en euros, par exemple 850 ou 850,50.");
        }
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Mise en page

export function PageHeader({ eyebrow, title, intro, actions }: { eyebrow?: string; title: ReactNode; intro?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {intro ? <p className="intro">{intro}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function Card({ title, actions, children, className = "", corne }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; corne?: boolean }) {
  return (
    <section className={`card ${corne ? "bv-corne" : ""} ${className}`}>
      {title || actions ? (
        <div className="card-head">
          {title ? <h2>{title}</h2> : <span />}
          {actions ? <div className="card-actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {hint ? <span className="stat-hint">{hint}</span> : null}
    </div>
  );
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {children ? <p className="muted">{children}</p> : null}
      {action}
    </div>
  );
}

export function Loading({ label = "Chargement…" }: { label?: string }) {
  return (
    <p className="loading" role="status">
      <span className="spinner" aria-hidden="true" /> {label}
    </p>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : "Erreur inattendue.";
  return (
    <div className="notice notice-erreur" role="alert">
      {message}
    </div>
  );
}

export function Notice({ tone = "info", title, children }: { tone?: "info" | "attention" | "erreur" | "succes" | "simulateur"; title?: ReactNode; children: ReactNode }) {
  return (
    <div className={`notice notice-${tone}`}>
      {title ? <strong className="block">{title}</strong> : null}
      {children}
    </div>
  );
}

export function Table({ children, caption }: { children: ReactNode; caption?: string }) {
  return (
    <div className="table-wrap">
      <table>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        {children}
      </table>
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { value: T; label: ReactNode }[] }) {
  return (
    <div className="tabs" role="tablist">
      {items.map((it) => (
        <button key={it.value} role="tab" type="button" aria-selected={it.value === value} className="tab" onClick={() => onChange(it.value)}>
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Statuts, sources, badges

export type CheckStatus = "conforme" | "a_confirmer" | "anomalie" | "manquant" | "info" | "non_concluant";
const STATUS: Record<CheckStatus, { label: string; cls: string; icon: ReactNode }> = {
  conforme: { label: "Conforme", cls: "conforme", icon: <path d="M8 12.5l2.7 2.7L16 9.8" /> },
  a_confirmer: { label: "À confirmer", cls: "a-confirmer", icon: <path d="M9.6 9.4a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.3M12 17h.01" /> },
  anomalie: { label: "Anomalie détectée", cls: "anomalie", icon: <path d="M12 8v5M12 16.5h.01" /> },
  manquant: { label: "Pièce manquante", cls: "manquant", icon: null },
  info: { label: "Information", cls: "info", icon: <path d="M12 11v5M12 8h.01" /> },
  non_concluant: { label: "Non concluant", cls: "manquant", icon: <path d="M8 12h8" /> },
};

export function StatusChip({ status, label }: { status: CheckStatus; label?: string }) {
  const s = STATUS[status] ?? STATUS.info;
  return (
    <span className={`bv-statut bv-statut-${s.cls}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9.5" strokeDasharray={status === "manquant" ? "3 3" : undefined} />
        {s.icon}
      </svg>
      {label ?? s.label}
    </span>
  );
}

export function Source({ doc, details = [], extrait }: { doc: string; details?: string[]; extrait?: boolean }) {
  return (
    <span className={`bv-source ${extrait ? "bv-source-extrait" : ""}`}>
      <b>{doc}</b>
      {details.map((d) => (
        <span key={d}>· {d}</span>
      ))}
    </span>
  );
}

export function Badge({ children, tone = "neutre" }: { children: ReactNode; tone?: "neutre" | "encre" | "succes" | "attention" | "erreur" | "simulateur" }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

/** Signale qu'une donnée vient d'un simulateur, jamais d'un service réel. */
export function SimBadge({ mode }: { mode?: string }) {
  if (!mode || mode === "production") return null;
  return <Badge tone="simulateur">{mode === "sandbox" ? "Sandbox" : "Simulateur"}</Badge>;
}

// ---------------------------------------------------------------------------
// Dépôt de fichiers

export function FileDrop({ onFiles, accept = "application/pdf,image/jpeg,image/png,image/webp", multiple, label = "Déposer un fichier", help, busy }: { onFiles: (files: File[]) => void; accept?: string; multiple?: boolean; label?: string; help?: ReactNode; busy?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const id = useId();
  return (
    <div
      className={`filedrop ${over ? "over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length) onFiles(multiple ? files : files.slice(0, 1));
      }}
    >
      <input
        ref={ref}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onFiles(files);
          e.target.value = "";
        }}
      />
      <label htmlFor={id} className="bv-btn bv-btn-secondaire bv-btn-petit">
        {busy ? <span className="spinner" aria-hidden="true" /> : null}
        {label}
      </label>
      <span className="muted small">{help ?? "PDF, JPEG, PNG ou WebP, 15 Mo au maximum. Vous pouvez aussi glisser le fichier ici."}</span>
    </div>
  );
}

export function FileLine({ name, size, href, right }: { name: string; size?: number; href?: string; right?: ReactNode }) {
  return (
    <div className="file-line">
      <span className="file-name">
        {href ? (
          <a href={href} target="_blank" rel="noreferrer">
            {name}
          </a>
        ) : (
          name
        )}
        {size !== undefined ? <span className="muted small"> · {fileSize(size)}</span> : null}
      </span>
      {right}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fenêtre modale et notifications

export function Modal({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="modal" onClose={onClose} aria-labelledby="modal-title">
      <div className="modal-head">
        <h2 id="modal-title">{title}</h2>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Fermer">
          ×
        </button>
      </div>
      <div className="modal-body">{children}</div>
      {footer ? <div className="modal-foot">{footer}</div> : null}
    </dialog>
  );
}

type Toast = { id: number; text: string; tone: "succes" | "erreur" | "info" };
const ToastCtx = createContext<(text: string, tone?: Toast["tone"]) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = (text: string, tone: Toast["tone"] = "succes") => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, text, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 4200);
  };
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast toast-${t.tone}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}

/** Petite liste définitionnelle clé / valeur. */
export function KeyValues({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {items.map(([k, v], i) => (
        <div key={i}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Étapes reliées par le fil de la charte. */
export function Fil({ steps }: { steps: { title: ReactNode; text?: ReactNode; state?: "fait" | "actuel" | "a-venir" }[] }) {
  return (
    <ol className="bv-fil">
      {steps.map((s, i) => (
        <li key={i} className={s.state === "fait" ? "bv-fil-fait" : s.state === "actuel" ? "bv-fil-actuel" : ""} aria-current={s.state === "actuel" ? "step" : undefined}>
          <span className="bv-fil-noeud">{String(i + 1).padStart(2, "0")}</span>
          <div>
            <p className="bv-fil-titre">{s.title}</p>
            {s.text ? <p className="bv-fil-texte">{s.text}</p> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Logo : version claire par défaut, version pour fond sombre en thème sombre ou sur un fond nuit (`inverse`). */
export function Logo({ inverse, height = 28 }: { inverse?: boolean; height?: number }) {
  const style = { height, width: "auto", display: "block" };
  if (inverse) return <img src="/bailivo-logo-horizontal-sur-fond-sombre.svg" alt="Bailivo" style={style} />;
  return (
    <picture>
      <source srcSet="/bailivo-logo-horizontal-sur-fond-sombre.svg" media="(prefers-color-scheme: dark)" />
      <img src="/bailivo-logo-horizontal.svg" alt="Bailivo" style={style} />
    </picture>
  );
}

/** Pour les formulaires : erreur d'un champ renvoyée par l'API. */
export function fieldError(err: unknown, field: string): string | undefined {
  return err instanceof ApiError ? err.fieldMessage(field) : undefined;
}
